import {
    AccessFilter,
    ManagedAgentSummaryResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { ChevronsUpDown } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { AgentAvatar } from '@/components/chat/MessageComposer/AgentAvatar';
import { AccessChecklist } from './AccessChecklist';
import { Button } from '@/components/ui/button';
import { FluidHoverLabelRow } from '@/components/ui/fluid-hover-row';
import { FluidHoverGroup } from '@/components/ui/fluid-hover-group';
import { ListSkeleton } from '@/components/ui/list-skeleton';
import { PillTabsList } from '@/components/ui/pill-tabs-list';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';
import { SearchBar } from '@/components/ui/search-bar';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';

interface ManagedAgent {
    guid: string;
    agentId: string;
    displayName: string;
}

// Collapses a (potentially long) agent list into a short label so the
// access column never has to grow wider than its cell.
const getAccessSummary = (
    currentAgents: any[],
    allAgents: ManagedAgent[]
): string => {
    const count = currentAgents.length;
    if (count === 0) return 'No access';
    if (allAgents.length > 0 && count === allAgents.length) return 'All';
    return `${count} agent${count === 1 ? '' : 's'}`;
};

const AGENT_ACCESS_TABS = [
    { value: 'aa-users', label: 'User access' },
    { value: 'aa-groups', label: 'Group access' },
    { value: 'aa-org', label: 'Organization' }
];

const FilterSelect = ({
    value,
    onChange
}: {
    value: AccessFilter;
    onChange: (v: AccessFilter) => void;
}) => (
    <Select value={value} onValueChange={(v) => onChange(v as AccessFilter)}>
        <SelectTrigger className="w-36">
            <SelectValue />
        </SelectTrigger>
        <SelectContent>
            <SelectItem value={AccessFilter.All}>All</SelectItem>
            <SelectItem value={AccessFilter.Yes}>Has access</SelectItem>
            <SelectItem value={AccessFilter.No}>No access</SelectItem>
        </SelectContent>
    </Select>
);

const Pagination = ({
    page,
    total,
    onPageChange
}: {
    page: number;
    total: number;
    onPageChange: (p: number) => void;
}) => (
    <div className="flex justify-between items-center mt-4 text-muted-foreground text-sm">
        <span>
            {total === 0
                ? 'No results'
                : `Showing ${(page - 1) * 10 + 1}–${Math.min(
                      page * 10,
                      total
                  )} of ${total} results`}
        </span>
        <div className="flex gap-2">
            <Button
                variant="tertiary"
                size="default"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
            >
                Previous
            </Button>
            <Button
                variant="tertiary"
                size="default"
                disabled={page * 10 >= total}
                onClick={() => onPageChange(page + 1)}
            >
                Next
            </Button>
        </div>
    </div>
);

const ManagedAgentsPopover = ({
    currentAgents,
    isOpen,
    onOpenChange,
    onSave,
    allAgents,
    pendingAgentGuids,
    setPendingAgentGuids,
    saving
}: {
    currentAgents: any[];
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (guids: string[]) => void;
    allAgents: ManagedAgent[];
    pendingAgentGuids: string[];
    setPendingAgentGuids: React.Dispatch<React.SetStateAction<string[]>>;
    saving: boolean;
}) => (
    <Popover
        open={isOpen}
        onOpenChange={(open) => {
            if (open)
                setPendingAgentGuids(currentAgents.map((a: any) => a.guid));
            onOpenChange(open);
        }}
    >
        <PopoverTrigger asChild>
            <Button
                variant="ghost"
                size="default"
                className="px-2 py-1.5 max-w-full h-auto font-normal text-muted-foreground hover:text-foreground text-sm"
            >
                <span
                    className="min-w-0 truncate"
                    title={
                        currentAgents.length === 0
                            ? undefined
                            : currentAgents
                                  .map((a: any) => a.displayName)
                                  .join(', ')
                    }
                >
                    {getAccessSummary(currentAgents, allAgents)}
                </span>
                <ChevronsUpDown className="shrink-0" />
            </Button>
        </PopoverTrigger>
        <PopoverContent className="bg-background p-0 w-80" align="start">
            <AccessChecklist
                title="Agents"
                items={allAgents.map((a) => ({
                    id: a.guid,
                    label: a.displayName,
                    leading: (
                        <AgentAvatar agentId={a.agentId} className="size-5" />
                    )
                }))}
                selectedIds={pendingAgentGuids}
                onSelectedIdsChange={setPendingAgentGuids}
                emptyText="No agents available"
                saving={saving}
                onCancel={() => onOpenChange(false)}
                onSave={onSave}
            />
        </PopoverContent>
    </Popover>
);

interface Props {
    mgmtApi: MgmtApi<unknown>;
}

export const ManagedAgentAccessTab: React.FC<Props> = ({ mgmtApi }) => {
    const [agentUsers, setAgentUsers] = useState<any[]>([]);
    const [agentUsersTotal, setAgentUsersTotal] = useState(0);
    const [agentUsersPage, setAgentUsersPage] = useState(1);
    const [agentUsersSearch, setAgentUsersSearch] = useState('');
    const [agentUsersDebouncedSearch, setAgentUsersDebouncedSearch] =
        useState('');
    const [agentUsersFilter, setAgentUsersFilter] = useState<AccessFilter>(
        AccessFilter.All
    );
    const [agentUsersLoading, setAgentUsersLoading] = useState(false);

    const [agentGroups, setAgentGroups] = useState<any[]>([]);
    const [agentGroupsTotal, setAgentGroupsTotal] = useState(0);
    const [agentGroupsPage, setAgentGroupsPage] = useState(1);
    const [agentGroupsSearch, setAgentGroupsSearch] = useState('');
    const [agentGroupsDebouncedSearch, setAgentGroupsDebouncedSearch] =
        useState('');
    const [agentGroupsFilter, setAgentGroupsFilter] = useState<AccessFilter>(
        AccessFilter.All
    );
    const [agentGroupsLoading, setAgentGroupsLoading] = useState(false);

    const [allAgents, setAllAgents] = useState<ManagedAgent[]>([]);
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [pendingAgentGuids, setPendingAgentGuids] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const [orgAgentGuids, setOrgAgentGuids] = useState<string[]>([]);
    const [orgAlias, setOrgAlias] = useState<string>('');
    const [orgName, setOrgName] = useState<string>('');
    const [orgLoading, setOrgLoading] = useState(false);
    const [orgSaving, setOrgSaving] = useState<string | null>(null);

    useEffect(() => {
        const t = setTimeout(() => {
            setAgentUsersDebouncedSearch(agentUsersSearch);
            setAgentUsersPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [agentUsersSearch]);

    useEffect(() => {
        const t = setTimeout(() => {
            setAgentGroupsDebouncedSearch(agentGroupsSearch);
            setAgentGroupsPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [agentGroupsSearch]);

    const mergeAgents = (incoming: ManagedAgent[]) => {
        setAllAgents((prev) => {
            const map = new Map(prev.map((a) => [a.guid, a]));
            incoming.forEach((a) => map.set(a.guid, a));
            return Array.from(map.values());
        });
    };

    useEffect(() => {
        mgmtApi.api
            .v1AccessManagedagentsList()
            .then((res) => setAllAgents(res?.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setOrgLoading(true);
        mgmtApi.api
            .v1AccessManagedagentsOrganizationsList()
            .then((res) => {
                const data = res?.data;
                if (data) {
                    setOrgAlias(data.alias);
                    setOrgName(data.name);
                    setOrgAgentGuids(
                        (data.managedAgents || []).map(
                            (a: ManagedAgentSummaryResponseModel) => a.guid
                        )
                    );
                }
            })
            .catch(console.error)
            .finally(() => setOrgLoading(false));
    }, [mgmtApi]);

    useEffect(() => {
        setAgentUsersLoading(true);
        mgmtApi.api
            .v1AccessManagedagentsUsersList({
                Page: agentUsersPage,
                Size: 10,
                Filter: agentUsersDebouncedSearch || undefined,
                AccessFilter: agentUsersFilter,
                IsDescending: false
            })
            .then((res) => {
                setAgentUsers(res?.data?.data || []);
                setAgentUsersTotal(res?.data?.rows || 0);
                (res?.data?.data || []).forEach((u: any) =>
                    mergeAgents(u.managedAgents || [])
                );
            })
            .catch(console.error)
            .finally(() => setAgentUsersLoading(false));
    }, [mgmtApi, agentUsersPage, agentUsersDebouncedSearch, agentUsersFilter]);

    useEffect(() => {
        setAgentGroupsLoading(true);
        mgmtApi.api
            .v1AccessManagedagentsGroupsList({
                Page: agentGroupsPage,
                Size: 10,
                Filter: agentGroupsDebouncedSearch || undefined,
                AccessFilter: agentGroupsFilter,
                IsDescending: false
            })
            .then((res) => {
                setAgentGroups(res?.data?.data || []);
                setAgentGroupsTotal(res?.data?.rows || 0);
                (res?.data?.data || []).forEach((g: any) =>
                    mergeAgents(g.managedAgents || [])
                );
            })
            .catch(console.error)
            .finally(() => setAgentGroupsLoading(false));
    }, [
        mgmtApi,
        agentGroupsPage,
        agentGroupsDebouncedSearch,
        agentGroupsFilter
    ]);

    const handleSaveUser = async (
        userId: string,
        managedAgentGuids: string[]
    ) => {
        setSaving(true);
        try {
            await mgmtApi.api.v1AccessManagedagentsUsersUpdate({
                userId,
                managedAgentGuids
            });
            setAgentUsers((prev) =>
                prev.map((u) =>
                    u.userId === userId
                        ? {
                              ...u,
                              managedAgents: allAgents.filter((a) =>
                                  managedAgentGuids.includes(a.guid)
                              )
                          }
                        : u
                )
            );
        } catch (e) {
            console.error('Failed to save managed agent user access', e);
        } finally {
            setSaving(false);
            setEditingUserId(null);
        }
    };

    const handleToggleOrgAgent = async (guid: string, enabled: boolean) => {
        const newGuids = enabled
            ? [...orgAgentGuids, guid]
            : orgAgentGuids.filter((id) => id !== guid);
        setOrgAgentGuids(newGuids);
        setOrgSaving(guid);
        try {
            await mgmtApi.api.v1AccessManagedagentsOrganizationsUpdate({
                alias: orgAlias,
                managedAgentGuids: newGuids
            });
        } catch (e) {
            console.error('Failed to save org managed agent access', e);
            setOrgAgentGuids(orgAgentGuids);
        } finally {
            setOrgSaving(null);
        }
    };

    const handleSaveGroup = async (
        groupId: string,
        managedAgentGuids: string[]
    ) => {
        setSaving(true);
        try {
            await mgmtApi.api.v1AccessManagedagentsGroupsUpdate({
                groupId,
                managedAgentGuids
            });
            setAgentGroups((prev) =>
                prev.map((g) =>
                    g.groupId === groupId
                        ? {
                              ...g,
                              managedAgents: allAgents.filter((a) =>
                                  managedAgentGuids.includes(a.guid)
                              )
                          }
                        : g
                )
            );
        } catch (e) {
            console.error('Failed to save managed agent group access', e);
        } finally {
            setSaving(false);
            setEditingGroupId(null);
        }
    };

    return (
        <div className="space-y-8">
            <section className="space-y-6">
                <SectionHeader
                    title="Agent Access"
                    description="Manage user and group access permissions for agents"
                />
                <Tabs defaultValue="aa-users">
                    <PillTabsList tabs={AGENT_ACCESS_TABS} className="mb-6" />

                    <TabsContent value="aa-users">
                        <div className="flex items-center gap-3 mb-4">
                            <SearchBar
                                value={agentUsersSearch}
                                onChange={(v) => {
                                    setAgentUsersSearch(v);
                                }}
                                placeholder="Search users..."
                            />
                            <FilterSelect
                                value={agentUsersFilter}
                                onChange={(v) => {
                                    setAgentUsersFilter(v);
                                    setAgentUsersPage(1);
                                }}
                            />
                        </div>
                        {agentUsersLoading ? (
                            <TableSkeleton columns={4} />
                        ) : (
                            <>
                                <div>
                                    <Table variant="fluid" className="table-fixed">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Display Name
                                                </TableHead>
                                                <TableHead>
                                                    Email
                                                </TableHead>
                                                <TableHead>
                                                    Agents
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {agentUsers.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={3}
                                                        className="py-8 text-muted-foreground text-center"
                                                    >
                                                        No users found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                agentUsers.map((user: any) => (
                                                    <TableRow key={user.userId}>
                                                        <TableCell
 className="font-medium truncate"
 title={user.displayName}
 >
 {user.displayName}
 </TableCell>
                                                        <TableCell
                                                            className="text-muted-foreground truncate"
                                                            title={user.email}
                                                        >
                                                            {user.email}
                                                        </TableCell>
                                                        <TableCell>
                                                            <ManagedAgentsPopover
                                                                currentAgents={
                                                                    user.managedAgents ||
                                                                    []
                                                                }
                                                                isOpen={
                                                                    editingUserId ===
                                                                    user.userId
                                                                }
                                                                onOpenChange={(
                                                                    open
                                                                ) =>
                                                                    setEditingUserId(
                                                                        open
                                                                            ? user.userId
                                                                            : null
                                                                    )
                                                                }
                                                                onSave={(
                                                                    guids
                                                                ) =>
                                                                    handleSaveUser(
                                                                        user.userId,
                                                                        guids
                                                                    )
                                                                }
                                                                allAgents={
                                                                    allAgents
                                                                }
                                                                pendingAgentGuids={
                                                                    pendingAgentGuids
                                                                }
                                                                setPendingAgentGuids={
                                                                    setPendingAgentGuids
                                                                }
                                                                saving={saving}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <Pagination
                                    page={agentUsersPage}
                                    total={agentUsersTotal}
                                    onPageChange={setAgentUsersPage}
                                />
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="aa-groups">
                        <div className="flex items-center gap-3 mb-4">
                            <SearchBar
                                value={agentGroupsSearch}
                                onChange={(v) => {
                                    setAgentGroupsSearch(v);
                                }}
                                placeholder="Search groups..."
                            />
                            <FilterSelect
                                value={agentGroupsFilter}
                                onChange={(v) => {
                                    setAgentGroupsFilter(v);
                                    setAgentGroupsPage(1);
                                }}
                            />
                        </div>
                        {agentGroupsLoading ? (
                            <TableSkeleton columns={3} />
                        ) : (
                            <>
                                <div>
                                    <Table variant="fluid" className="table-fixed">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Group Name
                                                </TableHead>
                                                <TableHead>
                                                    Members
                                                </TableHead>
                                                <TableHead>
                                                    Agents
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {agentGroups.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={3}
                                                        className="py-8 text-muted-foreground text-center"
                                                    >
                                                        No groups found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                agentGroups.map(
                                                    (group: any) => (
                                                        <TableRow
                                                            key={group.groupId}
                                                        >
                                                            <TableCell
                                                                className="font-medium truncate"
                                                                title={
                                                                    group.groupName
                                                                }
                                                            >
                                                                {
                                                                    group.groupName
                                                                }
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground">
                                                                {group.members ??
                                                                    '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <ManagedAgentsPopover
                                                                    currentAgents={
                                                                        group.managedAgents ||
                                                                        []
                                                                    }
                                                                    isOpen={
                                                                        editingGroupId ===
                                                                        group.groupId
                                                                    }
                                                                    onOpenChange={(
                                                                        open
                                                                    ) =>
                                                                        setEditingGroupId(
                                                                            open
                                                                                ? group.groupId
                                                                                : null
                                                                        )
                                                                    }
                                                                    onSave={(
                                                                        guids
                                                                    ) =>
                                                                        handleSaveGroup(
                                                                            group.groupId,
                                                                            guids
                                                                        )
                                                                    }
                                                                    allAgents={
                                                                        allAgents
                                                                    }
                                                                    pendingAgentGuids={
                                                                        pendingAgentGuids
                                                                    }
                                                                    setPendingAgentGuids={
                                                                        setPendingAgentGuids
                                                                    }
                                                                    saving={
                                                                        saving
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <Pagination
                                    page={agentGroupsPage}
                                    total={agentGroupsTotal}
                                    onPageChange={setAgentGroupsPage}
                                />
                            </>
                        )}
                    </TabsContent>
                    <TabsContent value="aa-org">
                        {orgLoading ? (
                            <ListSkeleton rows={4} />
                        ) : (
                            <div className="overflow-hidden">
                                <div className="px-5 py-4 border-border border-b">
                                    <p className="font-medium text-foreground text-base">
                                        {orgName || 'Organization'}
                                    </p>
                                    <p className="mt-0.5 text-muted-foreground text-sm">
                                        Grant all users in{' '}
                                        <span className="font-medium text-foreground">
                                            {orgName || 'the organization'}
                                        </span>{' '}
                                        access to the agents below.
                                    </p>
                                </div>
                                <FluidHoverGroup className="divide-y divide-border">
                                    {allAgents.map((a) => (
                                        <FluidHoverLabelRow
                                            key={a.guid}
                                            className="flex justify-between items-center gap-4 px-5 py-3.5"
                                        >
                                            <div className="flex items-center gap-3">
                                                <AgentAvatar
                                                    agentId={a.agentId}
                                                    className="size-6"
                                                />
                                                <span className="font-medium text-foreground text-sm">
                                                    {a.displayName}
                                                </span>
                                            </div>
                                            <Switch
                                                checked={orgAgentGuids.includes(
                                                    a.guid
                                                )}
                                                disabled={orgSaving === a.guid}
                                                onCheckedChange={(checked) =>
                                                    handleToggleOrgAgent(
                                                        a.guid,
                                                        checked
                                                    )
                                                }
                                            />
                                        </FluidHoverLabelRow>
                                    ))}
                                    {allAgents.length === 0 && (
                                        <div className="px-5 py-8 text-muted-foreground text-sm text-center">
                                            No agents available
                                        </div>
                                    )}
                                </FluidHoverGroup>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </section>
        </div>
    );
};
